import { useEffect } from 'react'
import { z } from 'zod'

import { usePositionRoleLevels, useTrajectoryPathManager } from '../hooks'

import type { AnyFieldApi } from '@tanstack/react-form'
import type { OrganizationRole } from '@/features/organization'
import type { AnyReactFormApi } from '@/types/form'
import { FieldInfo } from '@/components/common/field-info'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useConvexOrganizationRoles,
  useOrganizationManagers,
} from '@/features/organization/services/organization.convex'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ---------------------------------------------------------------------------
// Shared types & constants
// ---------------------------------------------------------------------------

export interface InviteFormData {
  email: string
  managerId: string
  positionRole: string
  positionLevel: string
  positionId: string
  roles: Array<string>
  path: string
}

export const INVITE_BASE_SCHEMA = z.object({
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  positionRole: z.string().min(1, 'Position role is required'),
  positionLevel: z.string().min(1, 'Position level is required'),
  path: z.string().min(1, 'Trajectory path is required'),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
})

const MEMBER_ROLE_KEY = 'member'

// ---------------------------------------------------------------------------
// Shared form prop type
// ---------------------------------------------------------------------------

interface InviteFormProps {
  form: AnyReactFormApi
}

// ---------------------------------------------------------------------------
// EmailField
// ---------------------------------------------------------------------------

export function EmailField({ form }: InviteFormProps) {
  return (
    <form.Field
      name="email"
      validators={{
        onDynamic: INVITE_BASE_SCHEMA.shape.email,
      }}
    >
      {(field: AnyFieldApi) => (
        <div className="space-y-2">
          <Label htmlFor={field.name}>Email Address</Label>
          <Input
            id={field.name}
            name={field.name}
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.handleChange(e.target.value)
            }
            type="email"
            placeholder="user@example.com"
          />
          <FieldInfo field={field} />
        </div>
      )}
    </form.Field>
  )
}

// ---------------------------------------------------------------------------
// PositionRoleLevelFields
// ---------------------------------------------------------------------------

export function PositionRoleLevelFields({ form }: InviteFormProps) {
  const { uniqueRoles, getLevelsForRole, findPositionId } =
    usePositionRoleLevels()

  return (
    <>
      {/* Position Role */}
      <form.Field
        name="positionRole"
        validators={{
          onDynamic: INVITE_BASE_SCHEMA.shape.positionRole,
        }}
      >
        {(field: AnyFieldApi) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Position Role</Label>
            <Select
              value={field.state.value}
              onValueChange={(value: string) => {
                field.handleChange(value)
                form.setFieldValue('positionLevel', '')
                form.setFieldValue('positionId', '')
              }}
            >
              <SelectTrigger id={field.name} className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {uniqueRoles.map((role: string) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldInfo field={field} />
          </div>
        )}
      </form.Field>

      {/* Position Level */}
      <form.Subscribe
        selector={(state: { values: InviteFormData }) =>
          state.values.positionRole
        }
      >
        {(selectedRole: string) => (
          <form.Field
            name="positionLevel"
            validators={{
              onDynamic: INVITE_BASE_SCHEMA.shape.positionLevel,
            }}
          >
            {(field: AnyFieldApi) => {
              const levels = selectedRole ? getLevelsForRole(selectedRole) : []

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Position Level</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value: string) => {
                      field.handleChange(value)
                      if (selectedRole) {
                        const positionId = findPositionId(selectedRole, value)
                        if (positionId) {
                          form.setFieldValue('positionId', positionId)
                        }
                      }
                    }}
                    disabled={!selectedRole}
                  >
                    <SelectTrigger id={field.name} className="w-full">
                      <SelectValue placeholder="Select a level" />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((levelOption) => (
                        <SelectItem
                          key={levelOption.id}
                          value={levelOption.level}
                        >
                          {levelOption.level} - {levelOption.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldInfo field={field} />
                </div>
              )
            }}
          </form.Field>
        )}
      </form.Subscribe>
    </>
  )
}

// ---------------------------------------------------------------------------
// TrajectoryPathField
// ---------------------------------------------------------------------------

export function TrajectoryPathField({ form }: InviteFormProps) {
  return (
    <form.Subscribe
      selector={(state: { values: InviteFormData }) => state.values.positionId}
    >
      {(selectedPositionId: string) => {
        const {
          trajectories,
          isLoading: isLoadingTrajectories,
          hasMultipleTrajectories,
          singleTrajectory,
          validateTrajectoryPath,
        } = useTrajectoryPathManager({
          positionId: selectedPositionId,
          form,
        })

        return (
          <form.Field
            name="path"
            validators={{
              onChange: ({ value }: { value: string }) =>
                validateTrajectoryPath(value),
            }}
          >
            {(field: AnyFieldApi) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Trajectory Path</Label>
                {hasMultipleTrajectories ? (
                  <Select
                    value={field.state.value}
                    onValueChange={(value: string) => field.handleChange(value)}
                    disabled={!selectedPositionId || isLoadingTrajectories}
                  >
                    <SelectTrigger id={field.name} className="w-full">
                      <SelectValue
                        placeholder={
                          isLoadingTrajectories
                            ? 'Loading trajectories...'
                            : 'Select a trajectory path'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {trajectories.map((trajectory) => (
                        <SelectItem key={trajectory.id} value={trajectory.path}>
                          {trajectory.path}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={field.name}
                    name={field.name}
                    value={
                      singleTrajectory
                        ? singleTrajectory.path
                        : field.state.value
                    }
                    readOnly={!!singleTrajectory}
                    disabled={!selectedPositionId || isLoadingTrajectories}
                    onBlur={field.handleBlur}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.handleChange(e.target.value)
                    }
                    placeholder={
                      isLoadingTrajectories
                        ? 'Loading trajectories...'
                        : 'No trajectory path available'
                    }
                    className={
                      singleTrajectory ? 'bg-muted cursor-not-allowed' : ''
                    }
                  />
                )}
                <FieldInfo field={field} />
                {selectedPositionId && isLoadingTrajectories && (
                  <p className="text-sm text-muted-foreground">
                    Loading trajectory paths...
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )
      }}
    </form.Subscribe>
  )
}

// ---------------------------------------------------------------------------
// RoleCheckboxesField
// ---------------------------------------------------------------------------

interface RoleCheckboxesFieldProps extends InviteFormProps {
  organizationRoles: Array<OrganizationRole>
  isLoadingRoles: boolean
  roleFilter?: (role: OrganizationRole) => boolean
}

export function RoleCheckboxesField({
  form,
  organizationRoles,
  isLoadingRoles,
  roleFilter,
}: RoleCheckboxesFieldProps) {
  // Auto-select member role when roles are loaded
  useEffect(() => {
    if (organizationRoles.length > 0) {
      const memberRole = organizationRoles.find(
        (r) => r.key === MEMBER_ROLE_KEY,
      )
      if (memberRole) {
        const currentRoles = form.getFieldValue('roles') || []
        if (!currentRoles.includes(memberRole.key)) {
          form.setFieldValue('roles', [memberRole.key])
        }
      }
    }
  }, [organizationRoles, form])

  const filteredRoles = roleFilter
    ? organizationRoles.filter(roleFilter)
    : organizationRoles

  return (
    <form.Field
      name="roles"
      validators={{
        onDynamic: INVITE_BASE_SCHEMA.shape.roles,
      }}
    >
      {(field: AnyFieldApi) => (
        <div className="space-y-2">
          <Label>Organization Roles</Label>
          <div className="space-y-2">
            <em className="text-sm text-muted-foreground">
              Select the roles for the employee. Roles can always be added and
              removed later.
            </em>
          </div>
          {isLoadingRoles ? (
            <div className="text-sm text-muted-foreground">
              Loading available roles...
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRoles
                .sort((a, b) => {
                  if (a.key === MEMBER_ROLE_KEY) return 1
                  if (b.key === MEMBER_ROLE_KEY) return -1
                  return 0
                })
                .map((role) => {
                  const isMemberRole = role.key === MEMBER_ROLE_KEY
                  return (
                    <div key={role.key} className="flex items-start space-x-2">
                      <Checkbox
                        id={`role-${role.key}`}
                        checked={field.state.value?.includes(role.key)}
                        disabled={isMemberRole}
                        className="mt-0.5"
                        onCheckedChange={(checked) => {
                          if (isMemberRole) return
                          const currentRoles = field.state.value || []
                          if (checked) {
                            field.handleChange([...currentRoles, role.key])
                          } else {
                            field.handleChange(
                              currentRoles.filter(
                                (r: string) => r !== role.key,
                              ),
                            )
                          }
                        }}
                      />
                      <label
                        htmlFor={`role-${role.key}`}
                        className={`flex flex-col gap-0.5 ${isMemberRole ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className="text-sm font-medium leading-none">
                          {role.name.charAt(0).toUpperCase() +
                            role.name.slice(1)}
                        </span>
                        {role.description && (
                          <span className="text-sm text-muted-foreground">
                            {role.description}
                          </span>
                        )}
                      </label>
                    </div>
                  )
                })}
            </div>
          )}
          <FieldInfo field={field} />
        </div>
      )}
    </form.Field>
  )
}

// ---------------------------------------------------------------------------
// Composite form content components (proper React components so hook calls
// are stable and not inside render-prop callbacks — fixes "Expected static
// flag was missing" React error).
// ---------------------------------------------------------------------------

/**
 * Form content for "Invite Direct Report" — fetches org roles at component
 * level so React can track hooks correctly.
 */
export function InviteDirectReportFormContent({
  form,
}: {
  form: AnyReactFormApi
}) {
  const { data: organizationRoles, isLoading: isLoadingRoles } =
    useConvexOrganizationRoles()

  return (
    <>
      <EmailField form={form} />
      <PositionRoleLevelFields form={form} />
      <TrajectoryPathField form={form} />
      <RoleCheckboxesField
        form={form}
        organizationRoles={organizationRoles}
        isLoadingRoles={isLoadingRoles}
      />
    </>
  )
}

interface InviteOrgUserFormContentProps {
  form: AnyReactFormApi
  organizationId: string | undefined
  isAdmin: boolean
}

/**
 * Form content for "Invite Organization User" — includes the manager
 * selector and fetches both roles and managers at component level.
 */
export function InviteOrgUserFormContent({
  form,
  organizationId,
  isAdmin,
}: InviteOrgUserFormContentProps) {
  const { data: organizationRoles, isLoading: isLoadingRoles } =
    useConvexOrganizationRoles()
  const { data: managers, isLoading: isLoadingManagers } =
    useOrganizationManagers(organizationId)

  return (
    <>
      <EmailField form={form} />

      {/* Manager Selector */}
      <form.Field name="managerId">
        {(field: AnyFieldApi) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Assign Manager</Label>
            <Select
              value={field.state.value}
              onValueChange={(value: string) => field.handleChange(value)}
              disabled={isLoadingManagers}
            >
              <SelectTrigger id={field.name} className="w-full">
                <SelectValue
                  placeholder={
                    isLoadingManagers
                      ? 'Loading managers...'
                      : 'Select a manager'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {managers.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    {manager.name || manager.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldInfo field={field} />
          </div>
        )}
      </form.Field>

      <PositionRoleLevelFields form={form} />
      <TrajectoryPathField form={form} />
      <RoleCheckboxesField
        form={form}
        organizationRoles={organizationRoles}
        isLoadingRoles={isLoadingRoles}
        roleFilter={
          isAdmin
            ? undefined
            : (r) =>
                !['admin', 'billing', 'analytics'].includes(
                  r.name.toLowerCase(),
                )
        }
      />
    </>
  )
}
